<script lang="ts">
  import { fetchRoomProductsByRoom } from '$lib/api';
  import ChannelAssignment from './ChannelAssignment.svelte';
  import { selectEntity } from '$lib/router';
  import { loadsQuery } from '$lib/queries/loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { LOAD_TYPES } from '$lib/constants/load';
  import type { LoadResponse } from '$lib/types/load';

  type RoomProductRow = {
    product_name: string;
    product_model: string;
    quantity: number;
    // total_price deliberately omitted — designers never see product prices
  };

  let { id }: { id: number } = $props();

  const unitLoads = loadsQuery();
  const unitRooms = roomsQuery();
  const unitZones = zonesQuery();

  let roomProducts: RoomProductRow[] = $state([]);
  let productsLoading = $state(true);

  const load = $derived((unitLoads.data ?? []).find((l) => l.id === id));
  const room = $derived(
    load ? (unitRooms.data ?? []).find((r) => r.id === load.room) : null
  );
  const zone = $derived(
    room ? (unitZones.data ?? []).find((z) => z.id === room.zone) : null
  );

  async function loadRoomProducts(roomId: number) {
    productsLoading = true;
    try {
      roomProducts = ((await fetchRoomProductsByRoom(roomId)) ?? []) as RoomProductRow[];
    } catch {
      roomProducts = [];
    } finally {
      productsLoading = false;
    }
  }

  $effect(() => {
    const roomId = load?.room;
    if (roomId) {
      void loadRoomProducts(roomId);
    } else {
      roomProducts = [];
      productsLoading = false;
    }
  });
</script>

<div class="p-3 space-y-4">
  {#if load}
    <header class="border-b border-border-default pb-3">
      <h2 class="text-base font-medium text-text-primary">{load.name}</h2>
      <div class="mt-1 flex flex-wrap items-center gap-2 text-sm">
        <span class="inline-flex px-2 py-0.5 rounded bg-bg-hover text-text-secondary text-xs">
          {LOAD_TYPES[load.load_type as keyof typeof LOAD_TYPES]?.label ?? load.load_type}
        </span>
        {#if zone && room}
          <button
            type="button"
            class="text-text-muted hover:text-text-primary transition-colors"
            onclick={() => selectEntity('room', room.id)}
          >
            {zone.name}
            <span class="mx-1">›</span>
            {room.name}
          </button>
        {/if}
      </div>
    </header>

    <ChannelAssignment {load} />

    <section>
      <h3 class="text-sm font-medium text-text-primary mb-2">Room products</h3>

      {#if productsLoading}
        <p class="text-sm text-text-muted">Loading products…</p>
      {:else if roomProducts.length === 0}
        <p class="text-sm text-text-muted">No products attached to this room.</p>
      {:else}
        <div class="border border-border-default rounded overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-bg-rail text-text-muted text-xs">
              <tr class="h-8">
                <th class="text-left px-3 font-medium">Product</th>
                <th class="text-left px-3 font-medium">Model</th>
                <th class="text-right px-3 font-medium w-16">Qty</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border-default">
              {#each roomProducts as product}
                <tr class="h-8">
                  <td class="px-3">{product.product_name}</td>
                  <td class="px-3 text-text-secondary font-mono text-xs">
                    {product.product_model}
                  </td>
                  <td class="px-3 text-right font-mono">{product.quantity}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
  {:else}
    <p class="text-sm text-text-muted">Load not found.</p>
  {/if}
</div>
